// "Customer changes" (spec 20261013 §12.3, ADR-0059): customers who asked, on
// the My Quotes page, to move the date or address of a booked collection.
// Staff rearrange the courier by hand, then "Apply and mark done" (Prism
// writes the booking through E1, for every row of the shipment) or "Refuse"
// with a reason the customer reads in their email.
//
// Prism owns every rule. This panel shows what was booked, what the customer
// asked for, and Prism's answer when it refuses an action.
import React, { useEffect, useRef, useState } from 'react'
import { api } from '../../utils/api'

const LIST_URL = '/api/deals/collection-change-requests?status=requested'
const resolveUrl = (id) => `/api/deals/collection-change-requests/${encodeURIComponent(id)}/resolve`

//: A10: every control a finger may press is at least 44px tall.
const TOUCH = { minHeight: 44 }

//: Prism's CHANGE_RESOLUTION_NOTE_MAX_LENGTH, the most the customer will read.
const NOTE_MAX_LENGTH = 500

//: Someone else resolved it first: the list is stale, so reload it.
const STALE_CODE = 'not_open'

const DAY = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' })
const ASKED = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
})

export default function CustomerChangesPanel({ onCountChange }) {
  const [requests, setRequests] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [busyIds, setBusyIds] = useState(() => new Set())
  const [rowError, setRowError] = useState(null) // { id, message }
  const [notice, setNotice] = useState(null)
  const [refusing, setRefusing] = useState(null) // { request, returnFocus }

  useEffect(() => { load() }, [])
  // The count follows the list, whichever update changed it.
  useEffect(() => { if (requests) onCountChange?.(requests.length) }, [requests])

  async function load() {
    try {
      const data = await api.get(LIST_URL)
      setLoadError(null)
      setRequests(data.requests || [])
    } catch (e) {
      setLoadError(e.message || 'Could not load customer changes')
    }
  }

  // Functional updates, so two resolves landing close together each remove
  // their own row from the current list, never from a stale copy.
  function removeRow(id) {
    setRequests((current) => current.filter((r) => r.collection_change_request_id !== id))
  }

  function setBusy(id, busy) {
    setBusyIds((current) => {
      const next = new Set(current)
      if (busy) next.add(id)
      else next.delete(id)
      return next
    })
  }

  // Answers null when resolved (or already resolved elsewhere, which reloads
  // the list), else the words to show. A refusal shows them in its dialog,
  // so `onRow` is false there and the row does not repeat them.
  async function resolve(request, body, { onRow = true } = {}) {
    const id = request.collection_change_request_id
    setBusy(id, true)
    setRowError(null)
    setNotice(null)
    try {
      await api.post(resolveUrl(id), body)
      removeRow(id)
      setNotice(`${request.quote_reference}: ${body.outcome === 'done' ? 'change applied' : 'change refused'}. The customer will get an email.`)
      return null
    } catch (e) {
      const message = e.message || 'Prism refused this change'
      if (e.body?.code !== STALE_CODE) {
        if (onRow) setRowError({ id, message })
        return message
      }
      setNotice(`${request.quote_reference}: ${message}`)
      await load()
      return null
    } finally {
      setBusy(id, false)
    }
  }

  async function refuse(note) {
    const message = await resolve(refusing.request, { outcome: 'refused', note }, { onRow: false })
    if (!message) closeRefuse()
    return message
  }

  function closeRefuse() {
    const target = refusing?.returnFocus
    setRefusing(null)
    target?.focus?.()
  }

  if (loadError) {
    return (
      <div role="alert" className="card" style={{ padding: 'var(--space-4)' }}>
        <p style={{ margin: 0 }}>{loadError}</p>
        <button className="btn btn-secondary" style={{ ...TOUCH, marginTop: 'var(--space-3)' }} onClick={load}>Try again</button>
      </div>
    )
  }
  if (requests === null) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><div className="loading-spinner" /></div>
  }

  return (
    <div>
      {notice && <p role="status" style={{ margin: '0 0 var(--space-4)' }}>{notice}</p>}
      {requests.length === 0 ? (
        <div className="card" style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
          <p style={{ margin: 0, fontWeight: 600 }}>No customer changes waiting</p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-secondary)' }}>
            When a customer asks to move a booked collection, it shows here.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Trade</th>
                <th scope="col">Customer</th>
                <th scope="col">Courier</th>
                <th scope="col">Booked</th>
                <th scope="col">Was</th>
                <th scope="col">Now</th>
                <th scope="col">Asked</th>
                <th scope="col" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <ChangeRow
                  key={request.collection_change_request_id}
                  request={request}
                  busy={busyIds.has(request.collection_change_request_id)}
                  error={rowError?.id === request.collection_change_request_id ? rowError.message : null}
                  onApply={() => resolve(request, { outcome: 'done' })}
                  onRefuse={(button) => setRefusing({ request, returnFocus: button })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {refusing && (
        <RefuseDialog
          request={refusing.request}
          busy={busyIds.has(refusing.request.collection_change_request_id)}
          onCancel={closeRefuse}
          onConfirm={refuse}
        />
      )}
    </div>
  )
}

function ChangeRow({ request, busy, error, onApply, onRefuse }) {
  const visit = request.send_type === 'warehouse_dropoff'
  return (
    <tr aria-label={`${request.quote_reference} ${request.customer_name || ''}`.trim()}>
      <td>
        <div style={{ fontWeight: 600 }}>{request.quote_reference}</div>
        {request.shipment_reference && (
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{request.shipment_reference}</div>
        )}
      </td>
      <td>{request.customer_name || 'No name'}</td>
      <td>{visit ? 'Warehouse visit' : (request.carrier || 'No courier yet')}</td>
      <td>
        <div>{bookedPhrase(request, visit)}</div>
        <BookingTags request={request} />
      </td>
      <td><ValuesList values={request.old_values} visit={visit} /></td>
      <td><ValuesList values={request.new_values} visit={visit} /></td>
      <td>{request.requested_at ? ASKED.format(new Date(request.requested_at)) : ''}</td>
      <td>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" style={TOUCH} disabled={busy} onClick={onApply}>
            {busy ? 'Working' : 'Apply and mark done'}
          </button>
          <button className="btn btn-secondary" style={TOUCH} disabled={busy} onClick={(e) => onRefuse(e.currentTarget)}>
            Refuse
          </button>
        </div>
        {error && <p role="alert" style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-danger)', maxWidth: 320 }}>{error}</p>}
      </td>
    </tr>
  )
}

// A7: the state is said in words, never colour alone.
function BookingTags({ request }) {
  if (request.booking_live !== false && !request.on_hold) return null
  return (
    <div style={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap', marginTop: 'var(--space-1)' }}>
      {request.booking_live === false && <span className="badge badge-error">Booking no longer live</span>}
      {request.on_hold && <span className="badge badge-warning">On hold</span>}
    </div>
  )
}

// Only the fields that change are present (Prism's ruling), so each is shown
// in its own line: the date (with the visit slot), then the address.
function ValuesList({ values, visit }) {
  const lines = []
  if (values?.collection_date) {
    lines.push(visit && 'dropoff_slot_start' in values
      ? `${dayPhrase(values.collection_date)}, ${slotPhrase(values.dropoff_slot_start)}`
      : dayPhrase(values.collection_date))
  }
  if (values?.address) lines.push(addressPhrase(values.address))
  return lines.map((line) => <div key={line}>{line}</div>)
}

function RefuseDialog({ request, busy, onCancel, onConfirm }) {
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)
  const dialogRef = useRef(null)
  const reasonRef = useRef(null)

  useEffect(() => { reasonRef.current?.focus() }, [])

  const trimmed = note.trim()

  async function submit() {
    setError(null)
    const message = await onConfirm(trimmed)
    if (message) setError(message)
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onCancel()
      return
    }
    if (e.key === 'Tab') trapFocus(e, dialogRef.current)
  }

  return (
    <div className="modal-overlay">
      <div
        ref={dialogRef}
        className="modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="refuse-change-title"
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="refuse-change-title">Refuse the change for {request.quote_reference}</h2>
        </div>
        <div className="modal-body-scroll" style={{ padding: 'var(--space-4)' }}>
          <p style={{ marginTop: 0 }}>
            The booking stays as it is. {request.customer_name || 'The customer'} gets an email with your reason.
          </p>
          <label htmlFor="refuse-change-reason" style={{ display: 'block', fontWeight: 600, marginBottom: 'var(--space-2)' }}>
            Reason (the customer will see this)
          </label>
          <textarea
            id="refuse-change-reason"
            ref={reasonRef}
            className="form-input"
            rows={4}
            maxLength={NOTE_MAX_LENGTH}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ width: '100%' }}
          />
          {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 0 }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" style={TOUCH} onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" style={TOUCH} disabled={!trimmed || busy} onClick={submit}>
            {busy ? 'Working' : 'Refuse change'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Keep Tab and Shift+Tab inside the dialog (A5).
function trapFocus(e, container) {
  const focusable = [...(container?.querySelectorAll('button:not([disabled]), textarea, input, select, [href]') || [])]
  if (focusable.length === 0) return
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (e.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
    e.preventDefault()
    last.focus()
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault()
    first.focus()
  }
}

function bookedPhrase(request, visit) {
  if (!request.booked_date) return 'No date'
  return visit ? `${dayPhrase(request.booked_date)}, ${slotPhrase(request.booked_slot_start)}` : dayPhrase(request.booked_date)
}

// "Thu 22 Oct" for "2026-10-22", read at noon UTC so no offset moves the day,
// and assembled from parts so no ICU version adds a comma.
function dayPhrase(isoDate) {
  const parts = Object.fromEntries(DAY.formatToParts(new Date(`${isoDate}T12:00:00Z`)).map((p) => [p.type, p.value]))
  return `${parts.weekday} ${parts.day} ${parts.month}`
}

// "10am" for "10:00:00"; no slot is the customer's "any time".
function slotPhrase(slotStart) {
  if (!slotStart) return 'any time'
  const hour = Number(String(slotStart).slice(0, 2))
  if (hour < 12) return `${hour}am`
  return hour === 12 ? '12pm' : `${hour - 12}pm`
}

// A legacy booking's old address is one string ({ text }).
function addressPhrase(address) {
  if (address.text) return address.text
  return [address.line_1, address.line_2, address.city, address.post_code].filter(Boolean).join(', ')
}
